import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Box,
} from "@mui/material";
import { SERVER_URL } from "../../constants";
import { bookingStatusName, inGroupScope, planCopy, rangeOfRecords } from "./utils/copyPlan";

// "500" tells nobody anything; the backend usually sends a message with it.
async function httpErrorText(res) {
  const text = await res.text().catch(() => "");
  let msg = "";
  try {
    const j = JSON.parse(text);
    msg = j?.message || j?.error || "";
  } catch {
    msg = text;
  }
  msg = String(msg || "").trim();
  return msg ? `${res.status} ${msg.slice(0, 160)}` : `HTTP ${res.status}`;
}

const isResidentialType = (pt) => {
  const s = String(pt ?? "").trim().toUpperCase();
  return s === "RESIDENTIAL" || s === "0";
};

// Copy one member's in-group bookings onto another OF THE SAME KIND (student↔student or
// staff↔staff) so the destination matches the source. "In-group" = the booking's campus
// matches the group campus and its dates overlap the group window; bookings outside that
// window are left untouched. The plan (add / adjust / remove) is shown before applying.
// The planning itself lives in utils/copyPlan.js, shared with the one→many copy.
export default function CopyBookingsDialog({
  open,
  onClose,
  token,
  showToast,
  dest, // { kind, id, key, name }
  candidates = [], // same-kind people [{ kind, id, key, name }]
  blocksByKey = {}, // key -> [normalised block]
  recordsByKey = {}, // key -> [{ start, end, campus }]
  groupStart = "",
  groupEnd = "",
  groupCampus = "",
  showFinancials = false,
  onApplied, // async (person) => void — refresh the destination row
}) {
  const [sourceKey, setSourceKey] = useState("");
  const [applying, setApplying] = useState(false);
  const [failures, setFailures] = useState([]); // what didn't land on the last apply

  useEffect(() => {
    if (open) {
      setSourceKey("");
      setFailures([]);
    }
  }, [open, dest?.key]);

  // Plan the changes to bring the destination's in-group bookings in line with source.
  const plan = useMemo(() => {
    if (!open || !dest || !sourceKey || sourceKey === dest.key) return null;
    const scope = { groupStart, groupEnd, groupCampus };
    return planCopy({
      src: (blocksByKey[sourceKey] || []).filter((b) => inGroupScope(b, scope)),
      dst: (blocksByKey[dest.key] || []).filter((b) => inGroupScope(b, scope)),
      destRange: rangeOfRecords(recordsByKey[dest.key]),
      mode: "match",
    });
  }, [open, dest, sourceKey, blocksByKey, recordsByKey, groupStart, groupEnd, groupCampus]);

  const nothingToDo = plan && !plan.toAdd.length && !plan.toAdjust.length && !plan.toRemove.length;

  // Remove → adjust → add, and keep going past a failed write: every step is
  // attempted, the failures are collected, and the destination is refreshed so
  // the grid shows what actually landed. Stopping at the first error left the
  // earlier writes in place and told the user only "Copy failed: … → 409".
  const apply = async () => {
    if (!plan || nothingToDo || !dest) return;
    setApplying(true);
    setFailures([]);
    const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
    const isAdult = dest.kind === "adult";
    const failed = [];
    const attempt = async (label, fn) => {
      try {
        const res = await fn();
        if (!res.ok) failed.push(`${label} (${await httpErrorText(res)})`);
      } catch (e) {
        failed.push(`${label} (${e.message})`);
      }
    };

    for (const b of plan.toRemove) {
      const url = isAdult
        ? `${SERVER_URL}api/adult-bookings/${b.id}`
        : `${SERVER_URL}api/bookings/${b.id}/product/${b.productId}`;
      await attempt(`remove ${b.productName}`, () => fetch(url, { method: "DELETE", headers: h }));
    }

    for (const a of plan.toAdjust) {
      const d = a.dest;
      await attempt(`adjust ${a.src.productName}`, () => {
        if (isAdult) {
          const r = d.raw || {};
          return fetch(`${SERVER_URL}api/adult-bookings/${d.id}`, {
            method: "PUT",
            headers: h,
            body: JSON.stringify({
              participationType: r.participationType || "SUPERVISE_ONLY",
              countsForTransport: r.countsForTransport ?? true,
              countsForRatio: r.countsForRatio ?? false,
              notes: r.notes ?? "",
              customStart: a.customStart,
              customEnd: a.customEnd,
              actualCharge: Number(a.src.actualCharge) || 0,
            }),
          });
        }
        return fetch(`${SERVER_URL}api/bookings/${d.id}`, {
          method: "PUT",
          headers: h,
          body: JSON.stringify({
            ...d.raw,
            actualCharge: Number(a.src.actualCharge) || 0,
            customStart: a.customStart,
            customEnd: a.customEnd,
          }),
        });
      });
    }

    for (const a of plan.toAdd) {
      const b = a.src;
      await attempt(`add ${b.productName}`, () => {
        if (isAdult) {
          const r = b.raw || {};
          return fetch(`${SERVER_URL}api/products/${b.productId}/adult-bookings`, {
            method: "POST",
            headers: h,
            body: JSON.stringify({
              adultId: dest.id,
              participationType:
                r.participationType || (isResidentialType(b.productType) ? "BOOKING" : "SUPERVISE_ONLY"),
              countsForTransport: r.countsForTransport ?? true,
              countsForRatio: r.countsForRatio ?? false,
              notes: r.notes ?? "",
              customStart: a.customStart,
              customEnd: a.customEnd,
              actualCharge: Number(b.actualCharge) || 0,
            }),
          });
        }
        return fetch(`${SERVER_URL}api/bookings/studentId/${dest.id}/productId/${b.productId}`, {
          method: "POST",
          headers: h,
          body: JSON.stringify({
            actualCharge: Number(b.actualCharge) || 0,
            bookingStatus: bookingStatusName(b.raw?.bookingStatus),
            customStart: a.customStart,
            customEnd: a.customEnd,
          }),
        });
      });
    }

    await onApplied?.(dest);
    setApplying(false);
    const total = plan.toAdd.length + plan.toAdjust.length + plan.toRemove.length;
    if (failed.length === 0) {
      showToast?.("Bookings copied", "success");
      onClose?.();
    } else {
      // Stay open so the list of what didn't land is read, not flashed.
      setFailures(failed);
      showToast?.(`${total - failed.length} of ${total} changes made — ${failed.length} failed`, "warning");
    }
  };

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy bookings to {dest?.name || "member"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Copies the chosen member&apos;s in-group bookings (matching the group campus and
            dates) onto <strong>{dest?.name || "this member"}</strong> — adding, adjusting and
            removing so they match. Only members of the same kind (
            {dest?.kind === "adult" ? "staff" : "students"}) are offered; bookings outside the
            group window are left alone.
          </Typography>

          <FormControl size="small" fullWidth>
            <InputLabel id="copy-src">Copy from</InputLabel>
            <Select
              labelId="copy-src"
              label="Copy from"
              value={sourceKey}
              onChange={(e) => setSourceKey(e.target.value)}
            >
              {candidates.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {failures.length > 0 && (
            <Alert severity="warning">
              {failures.length} change{failures.length === 1 ? "" : "s"} didn&apos;t land — the rest
              did, and the plan below is what&apos;s still left to do:
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                {failures.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </Box>
            </Alert>
          )}

          {plan &&
            (nothingToDo ? (
              <Alert severity="success">Already in sync — nothing to change.</Alert>
            ) : (
              <Box>
                {plan.toAdd.length > 0 && (
                  <>
                    <Typography variant="subtitle2" color="success.main">
                      Add
                    </Typography>
                    {plan.toAdd.map((a, i) => (
                      <Typography key={`add-${i}`} variant="body2">
                        + {a.src.productName} ({a.customStart} → {a.customEnd})
                      </Typography>
                    ))}
                  </>
                )}
                {plan.toAdjust.length > 0 && (
                  <>
                    <Typography variant="subtitle2" color="warning.main" sx={{ mt: 1 }}>
                      Adjust
                    </Typography>
                    {plan.toAdjust.map((a, i) => (
                      <Typography key={`adj-${i}`} variant="body2">
                        ~ {a.src.productName} → ({a.customStart} → {a.customEnd}
                        {showFinancials ? `, £${Number(a.src.actualCharge) || 0}` : ""})
                      </Typography>
                    ))}
                  </>
                )}
                {plan.toRemove.length > 0 && (
                  <>
                    <Typography variant="subtitle2" color="error.main" sx={{ mt: 1 }}>
                      Remove
                    </Typography>
                    {plan.toRemove.map((b, i) => (
                      <Typography key={`rem-${i}`} variant="body2">
                        − {b.productName} ({b.start} → {b.end})
                      </Typography>
                    ))}
                  </>
                )}
                {plan.skipped.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1, opacity: 0.7 }}>
                      Skipped
                    </Typography>
                    {plan.skipped.map((x, i) => (
                      <Typography key={`skip-${i}`} variant="body2" sx={{ opacity: 0.7 }}>
                        {x.src.productName} — {x.reason}
                      </Typography>
                    ))}
                  </>
                )}
              </Box>
            ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          Cancel
        </Button>
        <Button variant="contained" onClick={apply} disabled={!plan || nothingToDo || applying}>
          {applying ? "Applying…" : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
